import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { API } from './api';

const DEFAULT_VALUES = {
    'banner.envWarning.hide': false,
    'banner.publishingDashboardHeader.hide': false,
    'detection.workspace.defaultBoxColor': '#FFFFFF',
    'detection.workspace.showLabels': true,
    'detection.workspace.requireSaveConfirmation': true,
    'detection.workspace.predictBoundingBoxClass': false,
    'detection.workspace.predictionsEnabled': false,
    'detection.workspace.showPixelDimensions': true,
    'detection.workspace.grid.color': '#eea83b',
    'detection.workspace.grid.visible': false,
    'detection.workspace.grid.rows': 10,
    'detection.workspace.grid.columns': 10,
    'segmentation.workspace.showLabels': true,
    'segmentation.workspace.showAnnotations': true,
    'segmentation.workspace.noClassColor': '#FFFFFF',
    'segmentation.workspace.requireSaveConfirmation': true,
    'scenerecognition.workspace.grid.color': '#eea83b',
    'scenerecognition.workspace.grid.visible': false,
    'scenerecognition.workspace.grid.rows': 10,
    'scenerecognition.workspace.grid.columns': 10,
    'anomalyrecognition.workspace.grid.color': '#eea83b',
    'anomalyrecognition.workspace.grid.visible': false,
    'anomalyrecognition.workspace.grid.rows': 10,
    'anomalyrecognition.workspace.grid.columns': 10,
    'evaluateBrain.imageDetails.showROI': true,
    // The explainability setting values here should match the values in DEFAULT_EXPLAINABILITY_OVERLAY_SETTINGS.
    'evaluateBrain.imageDetails.showExplainabilityOverlay': true,
    'evaluateBrain.imageDetails.explainabilityOpacity': 70,
    'navSidebar.isExpanded': true,
    'evaluateBrain.classPerformance.sortingMethodsByDataset': {
        // This property will be populated over time with entries in this format:
        // datasetIdentifier: <SortingMethods.METHOD>
    },
    'evaluateBrain.staticBrain.hasViewedStaticBrainTab': {
        // This property will be populated over time with entries in this format:
        // datasetIdentifier: <boolean>
    },
    'evaluateBrain.hifiBrain.hasViewedHiFiBrainTab': {
        // This property will be populated over time with entries in this format:
        // datasetIdentifier: <boolean>
    },
    'deploy.upgradeBanner.hide': false,
    'library.sortOrder': {
        // This property will be populated over time with entries in this format:
        // datasetIdentifier: '{"sort": "...", "order": "[asc|desc]"}' // this is a stringified JSON object
    },
    'library.pagniator.limit': {
        // This property will be populated over time with entries in this format:
        // datasetIdentifier: <number>
    }
};

export enum SettingLifetime {
    Session = 'Session',
    Account = 'Account'
}

@Injectable()
export class SettingsProvider extends BaseProvider {
    private settings = {};

    constructor(private api: API) {
        super();
    }

    public async load(force: boolean = false): Promise<Object> {
        return await this.ensureLoaded(() => this.fetchSettings(), force);
    }

    private async fetchSettings(): Promise<Object> {
        try {
            const rawSettings = await this.api.get('users/settings');
            const sessionSettings = this.fetchLocalStorageSettings();

            return Object.assign(this.settings, rawSettings.message.settings, sessionSettings);
        } catch (err) {
            throw err;
        }
    }

    private fetchLocalStorageSettings(): Object {
        const currentSettings = JSON.parse(window.sessionStorage.getItem('settings') || '{}');
        return currentSettings;
    }

    public get(settingKey: string): any {
        if (this.settings.hasOwnProperty(settingKey)) {
            return this.settings[settingKey];
        } else if (DEFAULT_VALUES.hasOwnProperty(settingKey)) {
            return DEFAULT_VALUES[settingKey];
        }

        return null;
    }

    public async set(
        settingKey: string,
        settingValue: any,
        lifetime: SettingLifetime = SettingLifetime.Account
    ): Promise<void> {
        try {
            const allSettings = {};

            switch (lifetime) {
                case SettingLifetime.Session:
                    const currentSettings = this.fetchLocalStorageSettings();
                    Object.assign(allSettings, { [settingKey]: settingValue });

                    window.sessionStorage.setItem(
                        'settings',
                        JSON.stringify(Object.assign(currentSettings, { [settingKey]: settingValue }))
                    );
                    break;
                default:
                    const { message } = await this.api.put('users/settings', { [settingKey]: settingValue });
                    Object.assign(allSettings, message.settings);
                    break;
            }

            Object.assign(this.settings, allSettings);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Persist a setting value that is a JSON map object
     * This is a convenience method that updates the saved map objects
     */
    public async mapSet(
        settingKey: string,
        mapKey: string,
        settingValue: any,
        lifetime: SettingLifetime = SettingLifetime.Account
    ): Promise<void> {
        const mapObject = this.get(settingKey) || {};
        mapObject[mapKey] = settingValue;

        await this.set(settingKey, mapObject, lifetime);
    }

    /**
     * Get a setting value that is in a JSON map object using a specific key
     * This is a convenience method around the existing get method
     */
    public mapGet(settingKey: string, mapKey: string): any | undefined {
        const mapObject = this.get(settingKey);

        if (mapObject) {
            return mapObject[mapKey];
        } else {
            return undefined;
        }
    }

    /**
     * Set multiple user settings at once
     *
     * @param {Object} settingsObject Settings to save, passed as an object `settingsObject`.
     *  For example, to save a new value ('Value A') to the key settingKeyA,
     *  AND a value ('Value B') to the key settingKeyB, the `settingsObject` param would be `{settingKeyA: "Value A", settingKeyB: "Value B"}`
     * @memberof SettingsProvider
     */
    public async setAll(settingsObject: Object, lifetime: SettingLifetime = SettingLifetime.Account): Promise<void> {
        try {
            const allSettings = {};

            switch (lifetime) {
                case SettingLifetime.Session:
                    const currentSettings = this.fetchLocalStorageSettings();
                    Object.assign(allSettings, settingsObject);

                    window.sessionStorage.setItem(
                        'settings',
                        JSON.stringify(Object.assign(currentSettings, settingsObject))
                    );
                    break;
                default:
                    const { message } = await this.api.put('users/settings', settingsObject);
                    Object.assign(allSettings, message.settings);
                    break;
            }

            Object.assign(this.settings, allSettings);
        } catch (err) {
            throw err;
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/settings.ts